import {
	FIRST_CATEGORY_TRADE_MODAL_KEY_PREFIX,
	FIRST_PREDICTION_MODAL_KEY_PREFIX,
	SURVIVOR_INTRO_MODAL_SEEN,
	SURVIVOR_INTRO_REQUEST_SENT,
	USER_HAS_TRADES_KEY,
} from '@/constants';
import {
	useGetSurvivorIntroQuery,
	useGetUserTradesCountByCategoryQuery,
	useGetUserTradesCountQuery,
} from '@/store/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

const USER_HAS_TRADES_EVENT = 'user-has-trades';

export const getStoredHasTrades = () => {
	const stored = localStorage.getItem(USER_HAS_TRADES_KEY);
	if (stored === null) return null;
	return stored === '1' || stored === 'true';
};

export const markUserHasTrades = () => {
	localStorage.setItem(USER_HAS_TRADES_KEY, '1');
	window.dispatchEvent(new Event(USER_HAS_TRADES_EVENT));
};

export const getCategoryModalKey = (userId: any, categoryId: any) =>
	`${FIRST_CATEGORY_TRADE_MODAL_KEY_PREFIX}:${userId}:${categoryId}`;

export const isCategoryModalShown = (userId: any, categoryId: any) =>
	localStorage.getItem(getCategoryModalKey(userId, categoryId)) === '1';

export const markCategoryModalShown = (userId: any, categoryId: any) =>
	localStorage.setItem(getCategoryModalKey(userId, categoryId), '1');

export const clearTradeModalsStorage = () => {
	localStorage.removeItem(USER_HAS_TRADES_KEY);

	const keysToRemove = [];
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (
			key?.startsWith(`${FIRST_PREDICTION_MODAL_KEY_PREFIX}:`) ||
			key?.startsWith(`${FIRST_CATEGORY_TRADE_MODAL_KEY_PREFIX}:`)
		) {
			keysToRemove.push(key);
		}
	}
	keysToRemove.forEach((key: any) => localStorage.removeItem(key));
};

export const useCheckUserTradesByCategory = ({
	eventId,
	lobbyId,
	userId,
	categoryId,
	skip,
}: any) => {
	const modalAlreadyShown = categoryId && userId ? isCategoryModalShown(userId, categoryId) : null;

	const { data: tradesCount, isSuccess } = useGetUserTradesCountByCategoryQuery(
		eventId && !skip && modalAlreadyShown === false ? { eventId, lobbyId } : skipToken
	);

	useEffect(() => {
		if (!isSuccess || !categoryId || !userId) return;
		if (tradesCount > 0) {
			markCategoryModalShown(userId, categoryId);
		}
	}, [isSuccess, tradesCount, categoryId, userId]);

	return tradesCount;
};

export const useCheckUserTrades = (isUser: any) => {
	const [hasTrades, setHasTrades] = useState(() => getStoredHasTrades() === true);

	useEffect(() => {
		const handler = () => setHasTrades(true);
		window.addEventListener(USER_HAS_TRADES_EVENT, handler);
		return () => window.removeEventListener(USER_HAS_TRADES_EVENT, handler);
	}, []);

	const storedHasTrades = getStoredHasTrades();
	const { data: tradesCount, isSuccess } = useGetUserTradesCountQuery(
		storedHasTrades === null && isUser ? undefined : skipToken
	);

	useEffect(() => {
		if (!isSuccess) return;
		const hasAnyTrades = tradesCount > 0;
		localStorage.setItem(USER_HAS_TRADES_KEY, hasAnyTrades ? '1' : '0');
		setHasTrades(hasAnyTrades);
	}, [tradesCount, isSuccess]);

	return hasTrades;
};

const USER_SHOULD_SEE_SURVIVOR_INTRO = 'user_should_see_survivor_intro';

export const useCheckSurvivorIntro = (isRegisteredUser: any) => {
	const [showFirstSurvivorModal, setShowFirstSurvivorModal] = useState(false);
	const requestSentRef = useRef(false);

	const shouldShowIntro = useMemo(
		() => localStorage.getItem(USER_SHOULD_SEE_SURVIVOR_INTRO) !== 'false',
		[]
	);

	const modalAlreadySeen = localStorage.getItem(SURVIVOR_INTRO_MODAL_SEEN) === '1';
	const requestAlreadySent = localStorage.getItem(SURVIVOR_INTRO_REQUEST_SENT) === '1';

	const shouldSendRequest =
		isRegisteredUser && shouldShowIntro && !modalAlreadySeen && !requestAlreadySent;

	const {
		data: canShowIntro,
		isSuccess,
		isLoading,
		isFetching,
	} = useGetSurvivorIntroQuery(shouldSendRequest ? undefined : skipToken);

	useEffect(() => {
		if (!isSuccess || requestSentRef.current) return;

		requestSentRef.current = true;
		localStorage.setItem(SURVIVOR_INTRO_REQUEST_SENT, '1');
	}, [isSuccess]);

	useEffect(() => {
		if (isSuccess && canShowIntro === true && !modalAlreadySeen) {
			setShowFirstSurvivorModal(true);
		}

		if (isSuccess && canShowIntro === false) {
			localStorage.setItem(USER_SHOULD_SEE_SURVIVOR_INTRO, 'false');
		}
	}, [isSuccess, canShowIntro, modalAlreadySeen]);

	const closeSurvivorIntro = useCallback(() => {
		localStorage.setItem(SURVIVOR_INTRO_MODAL_SEEN, '1');
		localStorage.setItem(USER_SHOULD_SEE_SURVIVOR_INTRO, 'false');
		setShowFirstSurvivorModal(false);
	}, []);

	return {
		isUserHasSurvivorLobby: canShowIntro === true,
		showFirstSurvivorModal,
		closeSurvivorIntro,
		isLoading: isLoading || isFetching,
	};
};
